import { AnalysisConfig, EnhancedPattern, AnalysisResult, PatternDistribution } from '@/types/trading-analysis-types';
import { FuzzyMatcher } from '../algorithms/FuzzyMatcher';
import { WeightCalculator } from '../algorithms/WeightCalculator';
import { PatternClassifier } from '../utils/PatternClassifier';
import { BasicAnalyzer } from './BasicAnalyzer';

export class IntermediateAnalyzer extends BasicAnalyzer {
  constructor(config: AnalysisConfig) {
    super(config);
  }

  /**
   * Analyze patterns with fuzzy matching and enhanced weights
   */
  analyzePatterns(sequence: number[]): AnalysisResult {
    const startTime = Date.now();
    
    // Start with basic analysis
    const basicResult = super.analyzePatterns(sequence);
    let enhancedPatterns = basicResult.patterns;

      // Apply fuzzy matching if enabled
      if (this.config.similarityThreshold && this.config.similarityThreshold > 0) {
        enhancedPatterns = this.applyFuzzyMatching(enhancedPatterns, sequence);
      }

      // Apply enhanced weights
      if (this.config.enableRecencyWeights || this.config.enableLengthWeights) {
        enhancedPatterns = this.applyEnhancedWeights(enhancedPatterns, sequence.length);
      }

      // Re-classify patterns after enhancements
      enhancedPatterns.forEach(pattern => {
        pattern.classification = PatternClassifier.classifyPattern(pattern);
      });

    const processingTime = Date.now() - startTime;
    const fuzzyMatches = enhancedPatterns.filter(p => p.similarities && p.similarities.length > 0).length;

    // Calculate comprehensive metrics
    const patternDistribution = PatternClassifier.calculateDistribution(enhancedPatterns);
    const highRiskPatterns = PatternClassifier.getHighRiskPatterns(enhancedPatterns);
    const opportunityPatterns = PatternClassifier.getOpportunityPatterns(enhancedPatterns);

    return {
      mode: 'intermediate',
      patterns: enhancedPatterns,
      totalPatterns: enhancedPatterns.length,
      processingTime,
      fuzzyMatches,
      patternDistribution,
      highRiskPatterns,
      opportunityPatterns,
    };
  }

  /**
   * Apply fuzzy pattern matching
   */
  private applyFuzzyMatching(patterns: EnhancedPattern[], sequence: number[]): EnhancedPattern[] {
    const patternStrings = patterns.map(p => p.pattern);
    const threshold = this.config.similarityThreshold || 0.8;

    return patterns.map(pattern => {
      // Find similar patterns
      const similarities = FuzzyMatcher.findSimilarPatterns(
        pattern.pattern,
        patternStrings.filter(p => p !== pattern.pattern),
        threshold
      );

      // Merge statistics from similar patterns
      if (similarities.length > 0) {
        let totalWins = pattern.nextWinCount;
        let totalLosses = pattern.nextLossCount;
        let totalFrequency = pattern.frequency;

        similarities.forEach(sim => {
          const similarPattern = patterns.find(p => p.pattern === sim.pattern);
          if (similarPattern) {
            // Weight by similarity score
            const weight = sim.similarity;
            totalWins += similarPattern.nextWinCount * weight;
            totalLosses += similarPattern.nextLossCount * weight;
            totalFrequency += similarPattern.frequency * weight;
          }
        });

        const newSuccessRate = (totalWins / (totalWins + totalLosses)) * 100;

        return {
          ...pattern,
          nextWinCount: Math.round(totalWins),
          nextLossCount: Math.round(totalLosses),
          frequency: Math.round(totalFrequency),
          successRate: Math.round(newSuccessRate * 100) / 100,
          similarities,
        };
      }

      return pattern;
    });
  }

  /**
   * Apply enhanced weight calculations
   */
  private applyEnhancedWeights(patterns: EnhancedPattern[], sequenceLength: number): EnhancedPattern[] {
    return patterns.map(pattern => {
      const enhancedPattern = WeightCalculator.enhancePatternWithWeights(pattern, sequenceLength);
      
      // Recalculate confidence score with weights
      if (this.config.enableRecencyWeights || this.config.enableLengthWeights) {
        const combinedWeight = WeightCalculator.calculateCombinedWeight(
          enhancedPattern,
          sequenceLength,
          {
            recency: this.config.enableRecencyWeights ? 0.3 : 0,
            length: this.config.enableLengthWeights ? 0.3 : 0,
            frequency: 0.2,
            successRate: 0.2
          }
        );

        enhancedPattern.confidenceScore = Math.min(100, Math.round(combinedWeight * 100));
      }

      return enhancedPattern;
    }).sort((a, b) => (b.confidenceScore || 0) - (a.confidenceScore || 0));
  }

  /**
   * Enhanced prediction with fuzzy matching and weights
   */
   predictNextTrade(inputSequence: string, allPatterns: EnhancedPattern[]): {
     winProbability: number;
     lossProbability: number;
     confidenceLevel: 'very_low' | 'low' | 'medium' | 'high' | 'very_high';
     matchingPatterns: number;
     exactMatches: number;
     prediction: 'CERTAIN' | 'UNCERTAIN' | 'REFUSE';
     reason: string;
     fuzzyMatches?: number;
     debugInfo?: {
       datasetStats: { totalPatterns: number; winRate: number; totalSamples: number };
       exactMatches: Array<{ pattern: string; frequency: number; successRate: number; samples: number }>;
       rejectedMatches: Array<{ pattern: string; reason: string; frequency: number; successRate: number }>;
       qualityScore: number;
       biasWarning?: string;
     };
   } {
    const sequence = inputSequence.split(',').map(x => parseInt(x.trim()));
    
    if (sequence.some(x => isNaN(x) || (x !== 0 && x !== 1))) {
      throw new Error('Invalid sequence format');
    }

    if (allPatterns.length === 0) {
      throw new Error('No patterns available for prediction');
    }

    const inputString = sequence.join('');
    
    // Find exact matches first (using parent class logic for consistency)
    const exactMatches = allPatterns.filter(pattern => 
      inputString.endsWith(pattern.pattern)
    );

    // Find fuzzy matches
    const threshold = this.config.similarityThreshold || 0.8;
    const patternStrings = allPatterns.map(p => p.pattern);
    const fuzzyMatches = FuzzyMatcher.findMatchingPatterns(inputString, patternStrings, threshold);
    
    const fuzzyMatchedPatterns = fuzzyMatches
      .filter(match => match.matchType === 'fuzzy')
      .map(match => allPatterns.find(p => p.pattern === match.pattern))
      .filter(p => p !== undefined) as EnhancedPattern[];

    const allMatchingPatterns = [...exactMatches, ...fuzzyMatchedPatterns];

    // Apply same strict logic as BasicAnalyzer but include fuzzy matches
    const highQualityExactMatches = exactMatches.filter(p => 
      p.frequency >= 5 && 
      p.confidenceScore >= 60
    );

    const mediumQualityExactMatches = exactMatches.filter(p => 
      p.frequency >= 3 && 
      p.confidenceScore >= 40
    );

    // HIGH CONFIDENCE: Multiple high-quality exact matches
    if (highQualityExactMatches.length >= 2) {
      let totalWins = 0;
      let totalLosses = 0;

      highQualityExactMatches.forEach(pattern => {
        totalWins += pattern.nextWinCount;
        totalLosses += pattern.nextLossCount;
      });

      const winProbability = (totalWins / (totalWins + totalLosses)) * 100;
      const avgFrequency = highQualityExactMatches.reduce((sum, p) => sum + p.frequency, 0) / highQualityExactMatches.length;

      return {
        winProbability: Math.round(winProbability * 100) / 100,
        lossProbability: Math.round((100 - winProbability) * 100) / 100,
        confidenceLevel: avgFrequency >= 8 ? 'very_high' : 'high',
        matchingPatterns: exactMatches.length,
        exactMatches: highQualityExactMatches.length,
        prediction: 'CERTAIN',
        reason: `Found ${highQualityExactMatches.length} high-quality exact matches + ${fuzzyMatchedPatterns.length} fuzzy matches`,
        fuzzyMatches: fuzzyMatchedPatterns.length
      };
    }

    // MEDIUM CONFIDENCE: Use exact + fuzzy matches with enhanced weights
    if (mediumQualityExactMatches.length >= 1 || fuzzyMatchedPatterns.length >= 2) {
      const weightedResult = WeightCalculator.calculateWeightedPrediction(allMatchingPatterns);

      // Calculate confidence level with bonus for fuzzy matches
      const avgConfidence = allMatchingPatterns.reduce((sum, p) => sum + (p.confidenceScore || 0), 0) / allMatchingPatterns.length;
      const weightBonus = Math.min(15, weightedResult.totalWeight * 5);
      const fuzzyBonus = Math.min(10, fuzzyMatchedPatterns.length * 5);
      const adjustedConfidence = avgConfidence + weightBonus + fuzzyBonus;

      let confidenceLevel: 'very_low' | 'low' | 'medium' | 'high' | 'very_high' = 'low';
      if (adjustedConfidence >= 80) confidenceLevel = 'very_high';
      else if (adjustedConfidence >= 65) confidenceLevel = 'high';
      else if (adjustedConfidence >= 50) confidenceLevel = 'medium';
      else if (adjustedConfidence >= 35) confidenceLevel = 'low';
      else confidenceLevel = 'very_low';

      const prediction = exactMatches.length > 0 ? 'UNCERTAIN' : 'UNCERTAIN';
      const reason = `Found ${exactMatches.length} exact matches + ${fuzzyMatchedPatterns.length} fuzzy matches`;

      return {
        winProbability: weightedResult.winProbability,
        lossProbability: weightedResult.lossProbability,
        confidenceLevel,
        matchingPatterns: exactMatches.length,
        exactMatches: exactMatches.length,
        prediction,
        reason,
        fuzzyMatches: fuzzyMatchedPatterns.length
      };
    }

    // NO RELIABLE MATCHES: Calculate weighted average instead of 50-50
    if (allMatchingPatterns.length > 0) {
      console.log(`⚠️ LOW QUALITY MATCHES (Intermediate): Found ${exactMatches.length} exact + ${fuzzyMatchedPatterns.length} fuzzy matches - calculating weighted average`);
      
      // Calculate weighted average based on actual success rates and frequencies
      let totalWeightedWins = 0;
      let totalWeightedSamples = 0;
      let calculationDetails: string[] = [];
      
      allMatchingPatterns.forEach(p => {
        const weight = p.frequency; // Use frequency as weight
        const patternWins = (p.successRate / 100) * weight;
        totalWeightedWins += patternWins;
        totalWeightedSamples += weight;
        
        calculationDetails.push(`"${p.pattern}": ${p.successRate}% × ${weight} freq = ${patternWins.toFixed(2)} weighted wins`);
        console.log(`  📊 "${p.pattern}": freq=${p.frequency}, success=${p.successRate}%, weighted contribution=${patternWins.toFixed(2)}`);
      });
      
      const weightedWinProbability = totalWeightedSamples > 0 ? (totalWeightedWins / totalWeightedSamples) * 100 : 50;
      const weightedLossProbability = 100 - weightedWinProbability;
      
      console.log(`🧮 INTERMEDIATE WEIGHTED CALCULATION: ${totalWeightedWins.toFixed(2)} weighted wins / ${totalWeightedSamples} total weight = ${weightedWinProbability.toFixed(1)}% win probability`);
      
      return {
        winProbability: Math.round(weightedWinProbability * 100) / 100,
        lossProbability: Math.round(weightedLossProbability * 100) / 100,
        confidenceLevel: 'very_low',
        matchingPatterns: exactMatches.length,
        exactMatches: exactMatches.length,
        prediction: 'UNCERTAIN',
        reason: `Found ${exactMatches.length} exact + ${fuzzyMatchedPatterns.length} fuzzy matches with low quality - calculated weighted average (${weightedWinProbability.toFixed(1)}% win probability)`,
        fuzzyMatches: fuzzyMatchedPatterns.length
      };
    }

    // Truly no matches found
    return {
      winProbability: 50.0,
      lossProbability: 50.0,
      confidenceLevel: 'very_low',
      matchingPatterns: 0,
      exactMatches: 0,
      prediction: 'REFUSE',
      reason: 'No matches found - sequence has not been seen before',
      fuzzyMatches: 0
    };
  }
}